import React, { useEffect, useMemo, useState } from "react";
import { Save, X, Plus, Trash, Printer } from "lucide-react";
import { printMovement } from "../print/printMovement";
import { api } from "../../services/api";

const isSyntheticSerial = (s) => /^NS-\d+-\d+$/.test(String(s || "").trim());

export function ExitModule({ materials, brandItems = [], warehouses = [
  { id: 'popayan', name: 'Bodega Popayán' },
  { id: 'santander', name: 'Bodega Santander' }
], onRegister }) {
  const [exitType, setExitType] = useState("Daño");
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id || 'popayan');
  const [materialTypeFilter, setMaterialTypeFilter] = useState("Todos");
  const [materialId, setMaterialId] = useState(materials[0]?.id || "");
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState("");
  // Fase 3 del puente Almacén–Compras: centro de costo del consumo, para
  // poder reportar cuánto consume cada área mes a mes.
  const [costCenters, setCostCenters] = useState([]);
  const [costCenter, setCostCenter] = useState("");
  const [observations, setObservations] = useState("");
  const [serialOption, setSerialOption] = useState('No');
  const [serial, setSerial] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [serialRowsFromApi, setSerialRowsFromApi] = useState([]);
  const [serialsLoading, setSerialsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [statusMsg, setStatusMsg] = useState(null);
  const [lastReceipt, setLastReceipt] = useState(null);

  const exitTypes = ["Daño", "Donación", "Venta", "Traslado", "Vencimiento"];

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.accounting.costCenters.list({ limit: 2000, order: 'asc' });
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (active) setCostCenters(rows);
      } catch (_) {
        if (active) setCostCenters([]);
      }
    })();
    return () => { active = false; };
  }, []);

  const categoriesForWarehouse = useMemo(() => {
    const wh = String(warehouseId || "").trim();
    if (!wh) return [];
    const set = new Set();
    (materials || []).forEach((m) => {
      const stock = ((m.stockByWarehouse || {})[wh] || 0);
      if (String(m.status || "Activo") === "Activo" && stock > 0) {
        const t = String(m.type || m.category || "").trim();
        if (t) set.add(t);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [materials, warehouseId]);

  useEffect(() => {
    if (materialTypeFilter !== "Todos" && !categoriesForWarehouse.includes(materialTypeFilter)) {
      setMaterialTypeFilter("Todos");
    }
  }, [categoriesForWarehouse, materialTypeFilter]);

  const materialsForWarehouse = useMemo(() => {
    const wh = String(warehouseId || "").trim();
    return (materials || []).filter((m) => {
      const stock = ((m.stockByWarehouse || {})[wh] || 0);
      const typeOk = materialTypeFilter === "Todos" || String(m.type || m.category || "").trim() === String(materialTypeFilter).trim();
      return String(m.status || "Activo") === "Activo" && stock > 0 && typeOk;
    });
  }, [materials, warehouseId, materialTypeFilter]);

  const selectedMaterial = useMemo(() => {
    return (materials || []).find(m => String(m.id) === String(materialId)) || null;
  }, [materials, materialId]);

  const requiresSerialSelected = useMemo(() => {
    if (!selectedMaterial) return false;
    const reqDb = (selectedMaterial?.requires_serial === 1) || (String(selectedMaterial?.requires_serial || "").toLowerCase() === "true");
    return Boolean(selectedMaterial?.requiresSerial) || reqDb;
  }, [selectedMaterial]);

  const isSerialExit = useMemo(() => {
    return requiresSerialSelected || serialOption === "Sí";
  }, [requiresSerialSelected, serialOption]);

  useEffect(() => {
    if (requiresSerialSelected) setSerialOption("Sí");
  }, [requiresSerialSelected]);

  useEffect(() => {
    if (isSerialExit && Number(quantity) !== 1) setQuantity(1);
  }, [isSerialExit, quantity]);

  useEffect(() => {
    if (!materialsForWarehouse.some((m) => String(m.id) === String(materialId))) {
      setMaterialId(materialsForWarehouse[0]?.id || "");
      setSerial("");
      setSelectedBrand("");
    }
  }, [materialsForWarehouse, materialId]);

  useEffect(() => {
    const loadSerials = async () => {
      const matId = selectedMaterial?.id;
      const wh = String(warehouseId || "").trim();
      if (!matId || !wh) {
        setSerialRowsFromApi([]);
        return;
      }
      setSerialsLoading(true);
      try {
        const res = await api.serials.list({ material_id: matId, warehouse_id: wh });
        const data = Array.isArray(res?.data) ? res.data : (res?.data ? [res.data] : []);
        const seen = new Set();
        const unique = [];
        for (const r of data) {
          const s = String(r?.serial || "").trim();
          if (!s) continue;
          if (isSyntheticSerial(s)) continue;
          const k = s.toLowerCase();
          if (seen.has(k)) continue;
          seen.add(k);
          unique.push(r);
        }
        setSerialRowsFromApi(unique);
      } catch (_) {
        setSerialRowsFromApi([]);
      } finally {
        setSerialsLoading(false);
      }
    };
    loadSerials();
  }, [selectedMaterial, warehouseId]);

  const brandOptions = useMemo(() => {
    if (serialRowsFromApi.length > 0) {
      const seen = new Set();
      const unique = [];
      for (const r of serialRowsFromApi) {
        const nm = String(r.display_brand || r.brand || "").trim();
        if (!nm) continue;
        const key = nm.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push({ id: `brand-${key}`, name: nm });
      }
      return unique;
    }
    const code = String(selectedMaterial?.code || "").trim();
    const type = String(selectedMaterial?.type || selectedMaterial?.category || "").trim();
    const filtered = (brandItems || []).filter((b) => {
      if (String(b.status || "Activo") !== "Activo") return false;
      const bCode = String(b.materialCode || "").trim();
      const bType = String(b.materialType || "").trim();
      const codeOk = code ? (bCode === code) : true;
      const typeOk = type ? (bType === type) : true;
      return codeOk && typeOk;
    });
    const seen = new Set();
    const unique = [];
    for (const b of filtered) {
      const nm = String(b.name || "").trim();
      if (!nm) continue;
      const key = nm.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push({ id: b.id, name: nm });
    }
    return unique;
  }, [brandItems, selectedMaterial, serialRowsFromApi]);

  useEffect(() => {
    if (selectedBrand && !brandOptions.some((b) => String(b.name) === String(selectedBrand))) {
      setSelectedBrand("");
    }
  }, [brandOptions, selectedBrand]);

  const serialOptions = useMemo(() => {
    const filtered = selectedBrand
      ? serialRowsFromApi.filter((r) => String(r.display_brand || r.brand || "").trim() === String(selectedBrand).trim())
      : serialRowsFromApi;
    return filtered
      .map((r) => String(r.serial || "").trim())
      .filter((s) => s !== "");
  }, [serialRowsFromApi, selectedBrand]);

  const serialBrandByValue = useMemo(() => {
    const map = new Map();
    for (const r of serialRowsFromApi) {
      const s = String(r.serial || "").trim();
      if (!s) continue;
      const b = String(r.display_brand || r.brand || "").trim();
      if (b) map.set(s, b);
    }
    return map;
  }, [serialRowsFromApi]);

  useEffect(() => {
    if (serial && !serialOptions.includes(serial)) setSerial("");
  }, [serialOptions, serial]);

  useEffect(() => {
    if (serial && !selectedBrand) {
      const b = serialBrandByValue.get(serial);
      if (b) setSelectedBrand(b);
    }
  }, [serial, selectedBrand, serialBrandByValue]);

  const canSubmitSingle = useMemo(() => {
    const serialValid = isSerialExit ? !!serial.trim() : true;
    const qtyValid = isSerialExit ? Number(quantity) === 1 : Number(quantity) > 0;
    return warehouseId && materialId && qtyValid && exitType && reason.trim().length > 0 && serialValid && !!costCenter;
  }, [warehouseId, materialId, quantity, exitType, reason, serial, isSerialExit, costCenter]);

  const handleCancel = () => {
    setExitType("Daño");
    setWarehouseId(warehouses[0]?.id || 'popayan');
    setMaterialTypeFilter("Todos");
    setMaterialId(materials[0]?.id || "");
    setQuantity(0);
    setReason("");
    setObservations("");
    setSerialOption('No');
    setSerial('');
    setSelectedBrand('');
    setCostCenter('');
    setStatusMsg(null);
  };

  const handleAddItem = () => {
    if (!canSubmitSingle) return;
    const serialTrim = isSerialExit ? serial.trim() : "";
    const autoBrand = serialTrim ? (serialBrandByValue.get(serialTrim) || "") : "";
    const newItem = {
      exitType,
      warehouseId,
      materialId,
      quantity,
      reason,
      observations,
      serial: serialTrim,
      brandName: selectedBrand || autoBrand || '',
      costCenter
    };
    setItems(prev => [...prev, newItem]);
    setQuantity(0);
    setSerial('');
    setSelectedBrand('');
    setStatusMsg('Artículo agregado.');
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Si hay artículos acumulados, registrar todos; si no, registrar único
    const batch = items.length ? items : [{
      exitType,
      warehouseId,
      materialId,
      quantity,
      reason,
      observations,
      serial: isSerialExit ? serial.trim() : '',
      brandName: selectedBrand || (serialBrandByValue.get(serial.trim()) || ''),
      costCenter
    }];

    let okCount = 0;
    let lastMsg = '';
    let lastItem = null;
    for (const it of batch) {
      const res = await onRegister(it);
      lastMsg = res?.message || '';
      if (res?.success) okCount += 1;
      lastItem = it; // guarda el último ítem exitoso
    }
    setStatusMsg(okCount === batch.length ? (lastMsg || 'Operación realizada') : 'Algunos elementos no se registraron.');
    if (okCount === batch.length) {
      setItems([]);
      // Construir comprobante y disparar impresión automáticamente
      try {
        const material = materials.find(m => String(m.id) === String(lastItem.materialId));
        const wh = warehouses.find(w => String(w.id) === String(lastItem.warehouseId));
        const receipt = {
          type: 'Salida - ' + (lastItem.exitType || ''),
          date: new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' }),
          materialName: material?.name || String(lastItem.materialId),
          materialCode: material?.code,
          materialUnit: material?.unit,
          materialLocation: material?.location,
          materialStatus: material?.status,
          category: material?.category || material?.type || 'Material',
          quantity: parseInt(lastItem.quantity),
          reason: lastItem.reason,
          observations: lastItem.observations || '',
          serial: lastItem.serial || '',
          brandName: lastItem.brandName || '',
          warehouseId: lastItem.warehouseId || wh?.id || 'popayan',
          warehouseName: wh?.name,
          responsibleDoc: 'Sistema'
        };
        setLastReceipt(receipt);
        printMovement(receipt);
      } catch (e) {}
      handleCancel();
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="section-title mb-6">Salidas de Inventario</h3>

      {lastReceipt && (
        <div className="mb-4 p-3 border rounded bg-gray-50 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Movimiento registrado: {lastReceipt.type}</p>
            <p className="text-xs text-gray-600">{lastReceipt.materialName} — Cant: {lastReceipt.quantity} — {lastReceipt.date}</p>
          </div>
          <button type="button" onClick={() => printMovement(lastReceipt)} className="bg-gray-800 text-white px-3 py-2 rounded flex items-center gap-2">
            <Printer size={16} /> Imprimir comprobante
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Salida *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              value={exitType}
              onChange={(e) => setExitType(e.target.value)}
            >
              {exitTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bodega *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de material</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              value={materialTypeFilter}
              onChange={(e) => setMaterialTypeFilter(e.target.value)}
            >
              <option value="Todos">Todos</option>
              {categoriesForWarehouse.map((t) => (
                <option key={`type-${t}`} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Material *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              value={materialId}
              onChange={(e) => setMaterialId(e.target.value)}
            >
              <option value="">Seleccione...</option>
              {materialsForWarehouse.map((m) => {
                const stock = (m.stockByWarehouse || {})[warehouseId] || 0;
                return (
                  <option key={m.id} value={m.id}>
                    {m.code ? `${m.code} - ${m.name} (Stock: ${stock})` : `${m.id} - ${m.name} (Stock: ${stock})`}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cantidad *</label>
            <input
              type="number"
              min={0}
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="0"
              disabled={isSerialExit}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Motivo *</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Ej: Material obsoleto"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Centro de costo *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              value={costCenter}
              onChange={(e) => setCostCenter(e.target.value)}
            >
              <option value="">Seleccione...</option>
              {costCenters.map((cc) => (
                <option key={cc.id} value={cc.code || cc.name}>{cc.code ? `${cc.code} - ${cc.name}` : cc.name}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="border rounded-lg p-3 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serie</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={serialOption}
                    onChange={(e) => setSerialOption(e.target.value)}
                    disabled={requiresSerialSelected}
                  >
                    <option value="No">No</option>
                    <option value="Sí">Sí</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                  >
                    <option value="">(Opcional)</option>
                    {brandOptions.map((b) => (
                      <option key={b.id} value={b.name}>{b.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {isSerialExit && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serial a dar salida</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={serial}
                    onChange={(e) => setSerial(e.target.value)}
                    disabled={serialsLoading}
                  >
                    <option value="">{serialsLoading ? "Cargando..." : "Seleccione..."}</option>
                    {serialOptions.map((s) => (
                      <option key={`ser-${s}`} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Row 3 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones</label>
          <textarea
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            rows={4}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            placeholder="Detalles del daño, destino de la donación, etc..."
          />
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleAddItem}
            disabled={!canSubmitSingle}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> Agregar artículo
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center gap-2 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg"
          >
            <X className="h-4 w-4" /> Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmitSingle && items.length === 0}
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> Guardar
          </button>
        </div>

        {/* Lista de artículos */}
        {items.length > 0 && (
          <div className="mt-4 border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 text-left">Bodega</th>
                  <th className="p-2 text-left">Material</th>
                  <th className="p-2 text-left">Marca</th>
                  <th className="p-2 text-left">Serie</th>
                  <th className="p-2 text-left">Cant.</th>
                  <th className="p-2 text-left">Motivo</th>
                  <th className="p-2 text-left">Centro de costo</th>
                  <th className="p-2 text-left">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const m = materials.find(x => String(x.id) === String(it.materialId));
                  const w = warehouses.find(x => String(x.id) === String(it.warehouseId));
                  return (
                    <tr key={idx} className="border-t">
                      <td className="p-2">{w?.name || it.warehouseId}</td>
                      <td className="p-2">{m?.name || it.materialId}</td>
                      <td className="p-2">{it.brandName || ''}</td>
                      <td className="p-2">{it.serial || ''}</td>
                      <td className="p-2">{it.quantity}</td>
                      <td className="p-2">{it.reason}</td>
                      <td className="p-2">{it.costCenter || ''}</td>
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-800"
                        >
                          <Trash className="h-4 w-4" /> Quitar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Nota de estado */}

        {statusMsg && (
          <p className="text-sm text-gray-600 pt-2">{statusMsg}</p>
        )}
      </form>
    </div>
  );
}
